import { Pane } from 'tweakpane';
import Guides from '../../utils/Guides';
import { isDebug } from '../../core/Basics';

// Settings dels mòduls de la Scene (viuen als seus mòduls; aquí només ens hi lliguem).
import { WATER_SETTINGS, SEA_INTRO_FROM, SEA_INTRO_TO } from '../../../../3D/WaterPlane';
import { LIQUID_SETTINGS } from '../../../../3D/LiquidSphere';
import { FIREFLY_SETTINGS } from '../../../../3D/Fireflies';
import { OXIGEN_SETTINGS } from '../../../../3D/OxigenParticles';
import { STAR_SETTINGS } from '../../../../3D/StarField';
import { CLOUD_SETTINGS } from '../../../../3D/Clouds';
import { HAZE_SETTINGS } from '../../../../3D/HorizonHaze';
import { SUN_BODY_SETTINGS, MOON_SETTINGS } from '../../../../3D/CelestialBody';
import { RENDER_SETTINGS, FX_SETTINGS, BRAND_SETTINGS, NOISE_SETTINGS, TRANSITION_SETTINGS, BLOOM_SETTINGS, UNDERWATER_SETTINGS } from '../../../../3D/SceneCompositor';
import TimeOfDay, { LIGHT_SETTINGS, SUN_CYCLE, MOON_CYCLE, TIME_SETTINGS } from '../../../../3D/TimeOfDay';
import { GODRAYS_SETTINGS } from '../../../../3D/Godrays';
import { LIGHTCIRCLE_SETTINGS } from '../../../../3D/LightCircle';

// Pane de debug (tweakpane) singleton compartit. Cada escena munta/desmunta les seves carpetes
// aquí (mètodes mountXxx/unmountXxx); les escenes NOMÉS criden la funció, no construeixen la UI.
export default class DebugPane {
    static pane;
    static _sceneFolders = [];   // carpetes de 1r nivell de la Scene (per fer dispose net)
    static _sceneRaf = null;     // rAF del cicle 24h de la Scene

    static init() {
        return;
        if (!isDebug || this.pane) return;
        this.pane = new Pane({ title: 'Settings', expanded: true });
    }

    static dispose() {
        if (this.pane) {
            this.pane.dispose();
            this.pane = null;
        }
    }

    // ── SCENE ──────────────────────────────────────────────────────────────────
    // Munta TOTES les carpetes de la Scene i les lliga als seus *_SETTINGS. `scene` és
    // la classe Scene (estàtica): li llegim els items (_compositor, _water, _stars…) i
    // li cridem els mètodes (setHour, setExposure…). Així la scene queda neta.
    static mountScene(scene) {
        if (!isDebug) return;
        if (!this.pane) this.init();
        if (!this.pane) return;
        const pane = this.pane;
        const c = TimeOfDay.config(scene._resolveHour());
        const folders = [];   // carpetes de PRIMER NIVELL (per fer dispose net)
        const allF = [];      // TOTES les carpetes (per col·lapsar-les)
        const addF = (parent, title) => { const f = parent.addFolder({ title }); allF.push(f); return f; };

        // Guies (grid de columnes de layout) — debug global. Toggle = enabled + redibuix (com el shortcut).
        // Nota: mountScene corre ABANS de Guides.add() (main.js), així que _guides pot ser buit aquí →
        // per defecte ON (les guies s'afegeixen amb enabled:true); si ja existeixen, reflecteix el seu estat.
        // const guidesState = { on: Guides._guides.length ? !!Guides._guides[0].enabled : true };
        // const gGuides = pane.addBinding(guidesState, 'on', { label: 'guides (grid)' })
        //     .on('change', (e) => { Guides._guides.forEach((g) => { g.enabled = e.value; }); Guides.loop(); });
        // folders.push(gGuides); // primer nivell → es disposa net a unmountScene

        // Contenidors de primer nivell: SURFACE (cel/mar/sol/lluna/estrelles) i UNDERWATER (2n render).
        const underwater = addF(pane, 'Scene · Underwater'); folders.push(underwater);
        this._underwaterFolder = underwater;
        const surface = addF(pane, 'Scene · Surface'); folders.push(surface);

        // ── SURFACE · Time (origen de l'hora + scrubber + cicle 24h) ──
        const time = { hour: scene._resolveHour() };
        const fT = addF(surface, 'Time');
        // Origen de l'hora: real del dispositiu o fixa (per defecte 12).
        fT.addBinding(TIME_SETTINGS, 'useDeviceTime', { label: 'device real time' })
            .on('change', () => { time.hour = scene._resolveHour(); scene.setHour(time.hour); hourBinding.refresh(); });
        fT.addBinding(TIME_SETTINGS, 'fixedHour', { min: 0, max: 24, step: 0.5, label: 'fixed time' })
            .on('change', () => { if (!TIME_SETTINGS.useDeviceTime) { time.hour = TIME_SETTINGS.fixedHour; scene.setHour(time.hour); hourBinding.refresh(); } });
        const hourBinding = fT.addBinding(time, 'hour', { min: 0, max: 24, step: 0.1, label: 'time (preview)' })
            .on('change', (e) => scene.setHour(e.value));
        // Cicle complet de 24h en 15s: comença de nit (00) i acaba un altre cop a 00.
        const dayBtn = fT.addButton({ title: 'Play 24h cycle (15s)' });
        dayBtn.on('click', () => {
            if (this._sceneRaf) return; // ja s'està reproduint
            const DURATION = 15000;
            const t0 = performance.now();
            const step = (now) => {
                const p = Math.min((now - t0) / DURATION, 1);
                // Ease in-out (cubic) sobre el progrés → arrenca i frena suau, ràpid al mig.
                const e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
                time.hour = e * 24;            // 00 → 24 (= 00)
                scene.setHour(time.hour);
                hourBinding.refresh();
                if (p < 1) {
                    this._sceneRaf = requestAnimationFrame(step);
                } else {
                    this._sceneRaf = null;
                    time.hour = 0;
                    scene.setHour(0);
                    hourBinding.refresh();
                }
            };
            this._sceneRaf = requestAnimationFrame(step);
        });

        // ── SURFACE · Light (dia/nit): mateixa paleta, canvia la quantitat de llum. ──
        const fLi = addF(surface, 'Light (day/night)');
        const applyLight = () => scene.setHour(scene._lastHour ?? scene._resolveHour()); // re-aplica l'hora actual
        fLi.addBinding(LIGHT_SETTINGS, 'dayExposure', { min: 0.02, max: 0.6, step: 0.005, label: 'day light (peak)' }).on('change', applyLight);
        fLi.addBinding(LIGHT_SETTINGS, 'nightFloor', { min: 0.02, max: 0.4, step: 0.005, label: 'night light (base)' }).on('change', applyLight);
        fLi.addBinding(LIGHT_SETTINGS, 'moonBoost', { min: 0, max: 0.3, step: 0.005, label: 'moon light' }).on('change', applyLight);

        // ── SURFACE · Sun / Moon (cicle: hores i elevació de cada arc) + aparença dels discs ──
        const fSM = addF(surface, 'Sun / Moon');
        fSM.addBinding(SUN_CYCLE, 'rise', { min: 0, max: 24, step: 0.5, label: 'sun rise' }).on('change', applyLight);
        fSM.addBinding(SUN_CYCLE, 'set', { min: 0, max: 24, step: 0.5, label: 'sun set' }).on('change', applyLight);
        fSM.addBinding(SUN_CYCLE, 'peakHour', { min: 0, max: 24, step: 0.5, label: 'sun peak (h)' }).on('change', applyLight);
        fSM.addBinding(SUN_CYCLE, 'peakElev', { min: 2, max: 16, step: 0.5, label: 'sun peak (°)' }).on('change', applyLight);
        fSM.addBinding(MOON_CYCLE, 'rise', { min: 0, max: 24, step: 0.5, label: 'moon rise' }).on('change', applyLight);
        fSM.addBinding(MOON_CYCLE, 'set', { min: 0, max: 24, step: 0.5, label: 'moon set' }).on('change', applyLight);
        fSM.addBinding(MOON_CYCLE, 'peakHour', { min: 0, max: 30, step: 0.5, label: 'moon peak (h)' }).on('change', applyLight);
        fSM.addBinding(MOON_CYCLE, 'peakElev', { min: 2, max: 16, step: 0.5, label: 'moon peak (°)' }).on('change', applyLight);
        // Aparença del SOL (disc visible + lluentor)
        const applySun = () => { scene._sunBody?.setConfig({ ...SUN_BODY_SETTINGS }); applyLight(); };
        fSM.addBinding(SUN_BODY_SETTINGS, 'color', { label: 'sun color' }).on('change', applySun);
        fSM.addBinding(SUN_BODY_SETTINGS, 'size', { min: 60, max: 2000, step: 10, label: 'sun size' }).on('change', applySun);
        fSM.addBinding(SUN_BODY_SETTINGS, 'brightness', { min: 0, max: 4, step: 0.05, label: 'sun brightness' }).on('change', applySun);
        fSM.addBinding(SUN_BODY_SETTINGS, 'core', { min: 0.05, max: 0.5, step: 0.01, label: 'sun core' }).on('change', applySun);
        fSM.addBinding(SUN_BODY_SETTINGS, 'halo', { min: 0, max: 2, step: 0.05, label: 'sun halo weight' }).on('change', applySun);
        // Aparença de la LLUNA (disc visible + lluentor)
        const applyMoon = () => { scene._moonBody?.setConfig({ ...MOON_SETTINGS }); applyLight(); };
        fSM.addBinding(MOON_SETTINGS, 'color', { label: 'moon color' }).on('change', applyMoon);
        fSM.addBinding(MOON_SETTINGS, 'size', { min: 100, max: 2000, step: 10, label: 'moon size' }).on('change', applyMoon);
        fSM.addBinding(MOON_SETTINGS, 'brightness', { min: 0, max: 4, step: 0.05, label: 'moon brightness' }).on('change', applyMoon);
        fSM.addBinding(MOON_SETTINGS, 'core', { min: 0.05, max: 0.5, step: 0.01, label: 'moon core' }).on('change', applyMoon);
        fSM.addBinding(MOON_SETTINGS, 'glow', { min: 0.5, max: 8, step: 0.1, label: 'moon halo↓' }).on('change', applyMoon);
        fSM.addBinding(MOON_SETTINGS, 'halo', { min: 0, max: 2, step: 0.05, label: 'moon halo weight' }).on('change', applyMoon);

        // ── SURFACE · Sky (overrides en viu del cel atmosfèric) ──
        const sky = { exposure: scene._exposure, rayleigh: c.rayleigh, turbidity: c.turbidity, mie: c.mieCoefficient, gd: c.mieDirectionalG, elevation: c.sun.elevation, moonIntensity: c.moonIntensity };
        const applySky = () => {
            scene.setExposure(sky.exposure);
            scene.setSkyUniform('rayleigh', sky.rayleigh);
            scene.setSkyUniform('turbidity', sky.turbidity);
            scene.setSkyUniform('mieCoefficient', sky.mie);
            scene.setSkyUniform('mieDirectionalG', sky.gd);
            scene.setSunElevation(sky.elevation);
            scene.setMoonIntensity(sky.moonIntensity);
        };
        const fS = addF(surface, 'Sky');
        fS.addBinding(sky, 'exposure', { min: 0, max: 1.5, step: 0.01 }).on('change', applySky);
        fS.addBinding(sky, 'rayleigh', { min: 0, max: 4, step: 0.01 }).on('change', applySky);
        fS.addBinding(sky, 'turbidity', { min: 0, max: 20, step: 0.1 }).on('change', applySky);
        fS.addBinding(sky, 'mie', { min: 0, max: 0.03, step: 0.0005, label: 'mieCoeff' }).on('change', applySky);
        fS.addBinding(sky, 'gd', { min: 0.7, max: 0.999, step: 0.001, label: 'mieG (disc)' }).on('change', applySky);
        fS.addBinding(sky, 'elevation', { min: -5, max: 90, step: 0.5 }).on('change', applySky);
        fS.addBinding(sky, 'moonIntensity', { min: 0, max: 6, step: 0.05 }).on('change', applySky);

        // ── SURFACE · Sea (onades grans geomètriques + noise de superfície + posició) ──
        const fSea = addF(surface, 'Sea');
        const applySea = () => scene._water?.setConfig({ ...WATER_SETTINGS });
        const seaBindings = [
            // Onades geomètriques grans (la "forma" del mar)
            ['waveAmplitude', { min: 0, max: 600, step: 2, label: 'wave height' }],
            ['waveScale', { min: 0.2, max: 20, step: 0.1, label: 'wave frequency' }],
            ['waveSpeed', { min: 0, max: 8, step: 0.1, label: 'wave speed' }],
            ['waveChop', { min: 0, max: 1, step: 0.01, label: 'storm (spikey)' }],
            // Noise de superfície (el detall / arrissat)
            ['noiseSize', { min: 0.1, max: 30, step: 0.1, label: 'noise size' }],
            ['distortionScale', { min: 0, max: 10, step: 0.1, label: 'noise strength' }],
            ['timeSpeed', { min: 0, max: 0.02, step: 0.0002, label: 'noise speed' }],
            // Reflexos: més llums i ombres / matisos
            ['reflectionBoost', { min: 0, max: 2, step: 0.05, label: 'reflection (highlights)' }],
            ['fresnelPower', { min: 0.5, max: 8, step: 0.1, label: 'grazing reflection' }],
            ['specularStrength', { min: 0, max: 5, step: 0.05, label: 'sun sparkles' }],
            ['waveShade', { min: 0, max: 1.5, step: 0.05, label: 'wave shading' }],
            ['shadingContrast', { min: 0.5, max: 2.5, step: 0.05, label: 'sea contrast' }],
            // Posició / extensió / resolució
            ['seaY', { min: -2, max: 0.5, step: 0.01, label: 'height Y (general)' }],
            ['rotX', { min: -30, max: 30, step: 0.5, label: 'rotation X (°)' }],
            ['spread', { min: 1, max: 200, step: 1, label: 'extent (horizon)' }],
            // ['segments', { min: 8, max: 400, step: 4, label: 'resolution' }],
        ];
        seaBindings.forEach(([key, cfg]) => {
            if (WATER_SETTINGS[key] === undefined) return;
            fSea.addBinding(WATER_SETTINGS, key, cfg).on('change', applySea);
        });
        // Reprodueix TOTA l'animació d'entrada de la Home (framing + mar).
        fSea.addButton({ title: 'animate intro (Home)' }).on('click', () => scene.introHome());
        // Set MANUAL dels estats (salt instantani, sense animació) per inspeccionar-los / comparar.
        fSea.addButton({ title: 'set sea INITIAL' }).on('click', () => { scene._water?.setConfig({ ...SEA_INTRO_FROM }); fSea.refresh(); });
        fSea.addButton({ title: 'set sea FINAL' }).on('click', () => { scene._water?.setConfig({ ...WATER_SETTINGS }); fSea.refresh(); });

        // ── SURFACE · Stars (només de nit; l'opacitat la controla el nightFactor de l'hora) ──
        const fSt = addF(surface, 'Stars');
        const applyStars = () => scene._stars?.setConfig({ ...STAR_SETTINGS });
        fSt.addBinding(STAR_SETTINGS, 'color').on('change', applyStars);
        const starBindings = [
            ['count', { min: 100, max: 6000, step: 100 }],
            ['sizeMin', { min: 0.2, max: 8, step: 0.1 }],
            ['sizeMax', { min: 0.2, max: 12, step: 0.1 }],
            ['colorVariation', { min: 0, max: 1, step: 0.01 }],
            ['brightness', { min: 0, max: 3, step: 0.05 }],
            ['intensityMin', { min: 0, max: 1, step: 0.01 }],
            ['intensityMax', { min: 0, max: 1, step: 0.01 }],
            ['twinkleTimeMin', { min: 0.2, max: 15, step: 0.1 }],
            ['twinkleTimeMax', { min: 0.2, max: 15, step: 0.1 }],
            ['twinkleAmount', { min: 0, max: 1, step: 0.01 }],
            ['glowStrength', { min: 0, max: 3, step: 0.05 }],
            ['glowSize', { min: 0, max: 6, step: 0.1 }],
            ['radius', { min: 2000, max: 15000, step: 100 }],
            ['minElevationDeg', { min: 0, max: 60, step: 1 }],
        ];
        starBindings.forEach(([key, cfg]) => {
            if (STAR_SETTINGS[key] === undefined) return;
            fSt.addBinding(STAR_SETTINGS, key, cfg).on('change', applyStars);
        });

        // ── SURFACE · Clouds (núvols de noise que deriven pel cel) ──
        const fCl = addF(surface, 'Clouds');
        const applyClouds = () => scene._clouds?.setConfig({ ...CLOUD_SETTINGS });
        fCl.addBinding(CLOUD_SETTINGS, 'color', { label: 'color' }).on('change', applyClouds);
        [['coverage', { min: 0, max: 1, step: 0.01, label: 'coverage' }],
        ['softness', { min: 0.01, max: 0.6, step: 0.01, label: 'softness' }],
        ['opacity', { min: 0, max: 1, step: 0.01, label: 'opacity' }],
        ['scale', { min: 0.4, max: 5, step: 0.05, label: 'size (↑smaller)' }],
        ['speed', { min: 0, max: 3, step: 0.01, label: 'speed' }],
        ['dirX', { min: -1, max: 1, step: 0.05, label: 'dir X' }],
        ['dirY', { min: -1, max: 1, step: 0.05, label: 'dir Y' }],
        ['band', { min: 0.05, max: 0.5, step: 0.01, label: 'band' }],
        ['height', { min: -2, max: 5, step: 0.05, label: 'height' }],
        ['sizeX', { min: 2, max: 22, step: 0.25, label: 'plane width' }],
        ['sizeY', { min: 2, max: 14, step: 0.25, label: 'plane height' }],
        ['distance', { min: 1, max: 9, step: 0.1, label: 'distance' }],
        ].forEach(([k, cfg]) => {
            if (CLOUD_SETTINGS[k] === undefined) return;
            fCl.addBinding(CLOUD_SETTINGS, k, cfg).on('change', applyClouds);
        });

        // ── SURFACE · Haze (boirina densa a l'horitzó, enterboleix el llunyà) ──
        const fHz = addF(surface, 'Haze');
        const applyHaze = () => scene._haze?.setConfig({ ...HAZE_SETTINGS });
        fHz.addBinding(HAZE_SETTINGS, 'color', { label: 'color' }).on('change', applyHaze);
        [['density', { min: 0, max: 1, step: 0.01, label: 'density' }],
        ['band', { min: 0.02, max: 0.5, step: 0.01, label: 'band' }],
        ['softness', { min: 0.02, max: 0.6, step: 0.01, label: 'softness' }],
        ['offsetY', { min: -2, max: 2, step: 0.05, label: 'offset Y' }],
        ['speed', { min: 0, max: 0.5, step: 0.01, label: 'drift' }],
        ['sizeX', { min: 3, max: 22, step: 0.25, label: 'plane width' }],
        ['sizeY', { min: 2, max: 12, step: 0.25, label: 'plane height' }],
        ['distance', { min: 1, max: 8, step: 0.1, label: 'distance' }],
        ].forEach(([k, cfg]) => {
            if (HAZE_SETTINGS[k] === undefined) return;
            fHz.addBinding(HAZE_SETTINGS, k, cfg).on('change', applyHaze);
        });

        // ── UNDERWATER · Godrays (fake, per bloc block_junk). Settings globals → s'apliquen a tots els
        // godrays actius. La carpeta existeix sempre (encara que ara no hi hagi cap godray al DOM). ──
        const fG = addF(underwater, 'Godrays');
        const applyGodrays = () => scene._godrays.forEach((g) => g.setConfig({ ...GODRAYS_SETTINGS }));
        fG.addBinding(GODRAYS_SETTINGS, 'color', { label: 'color' }).on('change', applyGodrays);
        [
            ['intensity', { min: 0, max: 3, step: 0.05, label: 'intensity' }],
            ['noiseScale', { min: 0.5, max: 20, step: 0.1, label: 'columns' }],
            ['gap', { min: 0, max: 0.95, step: 0.01, label: 'gap' }],
            ['persist', { min: 0, max: 1, step: 0.02, label: 'persist (min rays)' }],
            ['contrast', { min: 0.5, max: 6, step: 0.1, label: 'sharpness' }],
            ['speed', { min: 0, max: 1, step: 0.01, label: 'speed' }],
            ['sway', { min: 0, max: 0.2, step: 0.005, label: 'sway' }],
            ['swayFreq', { min: 0, max: 12, step: 0.1, label: 'sway freq' }],
            ['converge', { min: 1, max: 3, step: 0.02, label: 'convergence' }],
            ['glow', { min: 0, max: 4, step: 0.05, label: 'glow' }],
            ['hot', { min: 0, max: 2, step: 0.02, label: 'white core' }],
            // COL·LOCACIÓ (estàtica, respecte del target; els data-attributes del target la sobreescriuen)
            ['rotate', { min: -90, max: 90, step: 1, label: 'rotation (°)' }],
            ['offsetX', { min: -2, max: 2, step: 0.01, label: 'offset X' }],
            ['offsetY', { min: -2, max: 2, step: 0.01, label: 'offset Y' }],
            ['scale', { min: 0.1, max: 4, step: 0.05, label: 'scale' }],
            ['scrollSpeed', { min: 0, max: 1, step: 0.01, label: 'scroll parallax' }], // 0 estàtic · 1 = 1:1
            ['scrollRotate', { min: -10, max: 10, step: 0.1, label: 'scroll rotation (°/screen)' }],
        ].forEach(([k, cfg]) => {
            if (GODRAYS_SETTINGS[k] === undefined) return;
            fG.addBinding(GODRAYS_SETTINGS, k, cfg).on('change', applyGodrays);
        });

        // ── UNDERWATER · Oxigen (partícules bombolla del 2n render; tot randomitzable min–max) ──
        const fP = addF(underwater, 'Oxygen');
        const applyOxigen = () => scene._particles?.setConfig({ ...OXIGEN_SETTINGS });
        fP.addBinding(OXIGEN_SETTINGS, 'color', { label: 'color' }).on('change', applyOxigen);
        // NOTA: els settings ESPACIALS són FRACCIONS de pantalla (× WIDTH/HEIGHT al mòdul), no px.
        // Per això els rangs són petits. Els NONE (opacitats, lerps, speed, blur, driftSpeed) són directes.
        [
            ['count', { min: 0, max: 0.002, step: 0.00001, label: 'density' }],
            // Mida i opacitat (random per partícula, min–max)
            ['sizeMin', { min: 0, max: 0.01, step: 0.0001, label: 'size min' }],
            ['sizeMax', { min: 0, max: 0.02, step: 0.0001, label: 'size max' }],
            ['opacityMin', { min: 0, max: 1, step: 0.01, label: 'opacity min' }],
            ['opacityMax', { min: 0, max: 1, step: 0.01, label: 'opacity max' }],
            // Flotació vertical (moviment PRINCIPAL, fracció d'alçada/s, random per partícula)
            ['floatMin', { min: 0, max: 0.2, step: 0.001, label: 'float min' }],
            ['floatMax', { min: 0, max: 0.2, step: 0.001, label: 'float max' }],
            // Deriva lateral (SECUNDÀRIA)
            ['driftX', { min: 0, max: 0.3, step: 0.001, label: 'drift X' }],
            ['driftZ', { min: 0, max: 0.3, step: 0.001, label: 'drift Z' }],
            ['driftSpeed', { min: 0, max: 3, step: 0.02, label: 'drift speed' }],
            // Displace de scroll: ritme del lerp per partícula (rang → parallax)
            // ['scrollLerpMin', { min: 0.01, max: 1, step: 0.01, label: 'scroll lerp min' }],
            // ['scrollLerpMax', { min: 0.01, max: 1, step: 0.01, label: 'scroll lerp max' }],
            ['scrollStrength', { min: 0, max: 2, step: 0.01, label: 'scroll strength' }],
            ['speed', { min: 0, max: 5, step: 0.05, label: 'global speed' }],
            // Bokeh (profunditat Z)
            ['focus', { min: -3, max: 3, step: 0.05 }],
            ['blur', { min: 0, max: 5, step: 0.05 }],
            // Volum d'emissió + forat central + marge (fraccions de pantalla)
            ['spreadX', { min: 0, max: 4, step: 0.02 }],
            ['spreadZ', { min: 0, max: 4, step: 0.02 }],
            ['holeX', { min: 0, max: 2, step: 0.01 }],
            ['holeY', { min: 0, max: 2, step: 0.01 }],
            ['edgeMargin', { min: 0, max: 1, step: 0.01, label: 'margin' }],
        ].forEach(([key, cfg]) => {
            if (OXIGEN_SETTINGS[key] === undefined) return;
            fP.addBinding(OXIGEN_SETTINGS, key, cfg).on('change', applyOxigen);
        });

        // ── UNDERWATER · Light circle (block_step). El color ve del data-color de cada target; aquí
        // controlem la FORMA (globals per a tots els cercles). ──
        const fLC = addF(underwater, 'Light circle');
        const applyLC = () => scene._lightCircles.forEach((c) => c.setConfig({ ...LIGHTCIRCLE_SETTINGS }));
        [['opacity', { min: 0, max: 1, step: 0.01, label: 'opacity' }],
        ['radius', { min: 0.2, max: 2, step: 0.01, label: 'radius' }],
        ['softness', { min: 0.05, max: 2, step: 0.01, label: 'blur' }],
        ['rotation', { min: 0, max: 5, step: 0.01, label: 'rotation' }],
        ].forEach(([k, cfg]) => {
            if (LIGHTCIRCLE_SETTINGS[k] === undefined) return;
            fLC.addBinding(LIGHTCIRCLE_SETTINGS, k, cfg).on('change', applyLC);
        });

        // Brand (grade corporatiu del cel+mar → paleta de la web) — primer nivell
        const fB = addF(pane, 'Scene · Brand');
        const applyBrand = () => scene._compositor?.applyBrand();
        fB.addBinding(BRAND_SETTINGS, 'strength', { min: 0, max: 1, step: 0.01, label: 'strength' }).on('change', applyBrand);
        fB.addBinding(BRAND_SETTINGS, 'gamma', { min: 0.2, max: 2.5, step: 0.05 }).on('change', applyBrand);
        fB.addBinding(BRAND_SETTINGS, 'dark', { label: 'dark (zenith/night)' }).on('change', applyBrand);
        fB.addBinding(BRAND_SETTINGS, 'mid', { label: 'mid (sea)' }).on('change', applyBrand);
        fB.addBinding(BRAND_SETTINGS, 'light', { label: 'light (horizon/day)' }).on('change', applyBrand);
        fB.addBinding(BRAND_SETTINGS, 'warmth', { min: 0, max: 1, step: 0.01, label: 'temperature' }).on('change', applyBrand);
        fB.addBinding(BRAND_SETTINGS, 'warm', { label: 'warm (sunset)' }).on('change', applyBrand);
        fB.addBinding(BRAND_SETTINGS, 'cool', { label: 'cool (night)' }).on('change', applyBrand);
        fB.addBinding(BRAND_SETTINGS, 'naturalPull', { min: 0, max: 1, step: 0.01, label: 'natural sunset' })
            .on('change', () => scene.setHour(scene._lastHour ?? scene._resolveHour()));
        // Fons underwater: degradat per scroll (clear a dalt de tot → dark al final de la pàgina).
        const applyUnderwaterBg = () => scene._compositor?.applyUnderwaterBg();
        fB.addBinding(UNDERWATER_SETTINGS, 'clear', { label: 'sea bg · clear (scroll 0)' }).on('change', applyUnderwaterBg);
        fB.addBinding(UNDERWATER_SETTINGS, 'dark', { label: 'sea bg · dark (scroll max)' }).on('change', applyUnderwaterBg);
        folders.push(fB);

        // Render (double render) — primer nivell, amb SUBCARPETES (Transició, Noise, FX).
        const fR = addF(pane, 'Scene · Render');
        fR.addBinding(RENDER_SETTINGS, 'split', { min: 0, max: 1, step: 0.01, label: 'split Y' })
            .on('change', (e) => scene.setSplit(e.value)); // Scene.setSplit sincronitza _split (l'skip de passos hi depèn)
        // Reenquadrament vertical del cel+mar (pitch de càmera al 1r render; no inclina l'esfera).
        fR.addBinding(RENDER_SETTINGS, 'seaFramingY', { min: -20, max: 20, step: 0.5, label: 'reframe Y (°)' })
            .on('change', (e) => scene._compositor?.setSeaFraming(e.value));
        fR.addBinding(RENDER_SETTINGS, 'parallax', { min: 0, max: 1, step: 0.01, label: 'parallax (Y)' })
            .on('change', (e) => scene._compositor?.setParallax(e.value));

        // ── Subcarpeta: Transició (la línia sea/underwater). El compositor ho llegeix cada frame → sense apply.
        const fTr = addF(fR, 'Transition');
        [['fade', { min: 0, max: 0.3, step: 0.001, label: 'fade (edges)' }],
        ['blur', { min: 0, max: 0.1, step: 0.002, label: 'blur' }],
        ['noise', { min: 0, max: 0.1, step: 0.002, label: 'noise' }],
        ['noiseScale', { min: 1, max: 30, step: 0.5, label: 'noise freq' }],
        ['sineAmp', { min: 0, max: 0.1, step: 0.002, label: 'sine amplitude' }],
        ['sineFreq', { min: 0.5, max: 20, step: 0.5, label: 'sine freq' }],
        ['animSpeed', { min: 0, max: 40, step: 0.5, label: 'animation (× progress)' }],
        ['aberration', { min: 0, max: 0.05, step: 0.001, label: 'aberration' }],
        ].forEach(([k, cfg]) => fTr.addBinding(TRANSITION_SETTINGS, k, cfg));

        // ── Subcarpeta: Noise (gra blue noise sobre tot el composite).
        const fN = addF(fR, 'Noise');
        const applyNoise = () => scene._compositor?.applyNoise();
        fN.addBinding(NOISE_SETTINGS, 'enabled', { label: 'noise' }).on('change', applyNoise);
        fN.addBinding(NOISE_SETTINGS, 'intensity', { min: 0, max: 0.5, step: 0.005, label: 'opacity' }).on('change', applyNoise);
        fN.addBinding(NOISE_SETTINGS, 'scale', { min: 0.5, max: 8, step: 0.1, label: 'grain (size px)' }).on('change', applyNoise);
        fN.addBinding(NOISE_SETTINGS, 'speed', { min: 0, max: 1, step: 0.01, label: 'animation' }).on('change', applyNoise);
        fN.addBinding(NOISE_SETTINGS, 'fxBoost', { min: 0, max: 6, step: 0.1, label: 'FX (edges)' }).on('change', applyNoise);
        fN.addBinding(NOISE_SETTINGS, 'color', { label: 'color' }).on('change', applyNoise);

        // ── Subcarpeta: FX (aberració cromàtica + blur focal) — apliquen a TOT el render.
        const fFx = addF(fR, 'FX');
        const applyFx = () => scene._compositor?.applyFx();
        fFx.addBinding(FX_SETTINGS, 'enabled', { label: 'FX' }).on('change', applyFx);
        [['radius', { min: 0, max: 1, step: 0.01, label: 'fx radius' }],
        ['abIntensity', { min: 0, max: 3, step: 0.05, label: 'aberr strength' }],
        ['abDisplace', { min: 0, max: 0.05, step: 0.0005, label: 'aberr displace' }],
        ['abDirectional', { min: 0, max: 2, step: 0.05, label: 'aberr directional' }],
        ['blurIntensity', { min: 0, max: 3, step: 0.05, label: 'blur strength' }],
        ['blurRadius', { min: 0, max: 0.1, step: 0.001, label: 'blur radius' }],
        ].forEach(([key, cfg]) => {
            if (FX_SETTINGS[key] === undefined) return;
            fFx.addBinding(FX_SETTINGS, key, cfg).on('change', applyFx);
        });

        // ── Bloom (glow dels godrays + oxigen particles, underwater). El compositor llegeix
        //    BLOOM_SETTINGS cada frame → no cal apply. ──
        const fBl = addF(fR, 'Bloom');
        fBl.addBinding(BLOOM_SETTINGS, 'enabled', { label: 'bloom' });
        // Selecció de layers + intensitat per tipus (per veure combinacions).
        fBl.addBinding(BLOOM_SETTINGS, 'particles', { label: '· oxygen particles' });
        fBl.addBinding(BLOOM_SETTINGS, 'particlesIntensity', { min: 0, max: 4, step: 0.05, label: '   particles intensity' });
        fBl.addBinding(BLOOM_SETTINGS, 'godrays', { label: '· godrays' });
        fBl.addBinding(BLOOM_SETTINGS, 'godraysIntensity', { min: 0, max: 4, step: 0.05, label: '   godrays intensity' });
        fBl.addBinding(BLOOM_SETTINGS, 'lightcircles', { label: '· lightcircles' });
        fBl.addBinding(BLOOM_SETTINGS, 'lightcirclesIntensity', { min: 0, max: 4, step: 0.05, label: '   lightcircles intensity' });
        [['intensity', { min: 0, max: 4, step: 0.05, label: 'intensity (global)' }],
        ['radius', { min: 0.2, max: 4, step: 0.1, label: 'width (radius)' }],
        ['iterations', { min: 1, max: 10, step: 1, label: 'steps (softness)' }],
        ].forEach(([key, cfg]) => fBl.addBinding(BLOOM_SETTINGS, key, cfg));

        folders.push(fR);

        // Totes les carpetes (i sub-carpetes) tancades per defecte.
        allF.forEach((f) => { f.expanded = false; });

        this._sceneFolders = folders; // només les de primer nivell (el dispose baixa a les filles)
        if (scene._liquid) this.mountSphere(scene);
    }

    // ── SPHERE (+ Fireflies) — es munta/desmunta amb l'esfera (Scene.addSphere/removeSphere), no amb
    // la Scene: l'esfera la crea l'scroll-item del bloc que té el target, després de mountScene. ──
    static mountSphere(scene) {
        if (!isDebug || !this._underwaterFolder) return;
        this.unmountSphere();
        const underwater = this._underwaterFolder;
        const folders = [];
        const addF = (parent, title) => {
            const f = parent.addFolder({ title, expanded: false });
            if (parent === underwater) folders.push(f);
            return f;
        };
        // NOTA: els settings SCALE són FRACCIÓ × Metrics.HEIGHT.
        if (scene._liquid) {
            const fL = addF(underwater, 'Sphere');
            const applyLiquid = () => scene._liquid?.setConfig({ ...LIQUID_SETTINGS });
            const addLiquidGroup = (parent, title, bindings) => {
                const fG = addF(parent, title);
                bindings.forEach(([key, cfg]) => {
                    if (LIQUID_SETTINGS[key] === undefined) return;
                    fG.addBinding(LIQUID_SETTINGS, key, cfg).on('change', applyLiquid);
                });
                return fG;
            };

            // mida (particleRadius), quantitat (count) i radius (spawnRadius) són tres valors
            // totalment independents — cap es deriva dels altres.
            addLiquidGroup(fL, 'General', [
                ['count', { min: 500, max: 40000, step: 100, label: 'quantitat' }],
                ['spawnRadius', { min: 0, max: 2, step: 0.005, label: 'mass radius' }],
                ['particleRadius', { min: 0, max: 0.05, step: 0.0005, label: 'particle size' }],
                ['particleDetail', { min: 0, max: 3, step: 1, label: 'particle detail' }],
                ['shellBias', { min: 1, max: 10, step: 0.1, label: 'shell bias (centre buit)' }],
            ]);

            addLiquidGroup(fL, 'Floating', [
                ['floatAmplitude', { min: 0, max: 1, step: 0.001, label: 'amplitude' }],
                ['floatSpeed', { min: 0, max: 2, step: 0.01, label: 'speed' }],
            ]);

            // Deformació GLOBAL: capa independent (veure capçalera de LiquidSphere.js) — NO és
            // pulsació/respiració, deforma diferents zones de la massa en direccions diferents.
            addLiquidGroup(fL, 'Deformation', [
                ['waveStrength', { min: 0, max: 0.15, step: 0.001, label: 'strength' }],
                ['waveFrequency', { min: 0, max: 0.05, step: 0.0005, label: 'frequency' }],
                ['waveSpeed', { min: 0, max: 2, step: 0.01, label: 'speed' }],
                ['waveFalloff', { min: 0.2, max: 5, step: 0.1, label: 'falloff (center)' }],
            ]);

            addLiquidGroup(fL, 'Fluid', [
                ['splatRadius', { min: 0.01, max: 0.3, step: 0.002, label: 'splat radius' }],
                ['splatForce', { min: 0, max: 60, step: 0.5, label: 'splat force (1/s)' }],
                ['splatGain', { min: 0, max: 4, step: 0.05, label: 'splat gain (energia)' }],
                ['velocityDissipation', { min: 0, max: 5, step: 0.05, label: 'dissipation (1/s)' }],
                ['vorticity', { min: 0, max: 60, step: 0.5, label: 'vorticity (remolins)' }],
                ['pressureIterations', { min: 1, max: 60, step: 1, label: 'pressure iter (abast)' }],
            ]);

            addLiquidGroup(fL, 'Flow response', [
                ['flowStrength', { min: 0, max: 2, step: 0.01, label: 'strength' }],
                ['coupling', { min: 0.2, max: 30, step: 0.1, label: 'coupling (inèrcia)' }],
                ['depthLag', { min: 0, max: 1, step: 0.01, label: 'depth lag' }],
                ['settleRate', { min: 0.05, max: 6, step: 0.05, label: 'settle (1/s)' }],
                ['maxDisplacement', { min: 0, max: 0.3, step: 0.002, label: 'max displacement' }],
                ['silhouetteLock', { min: 0, max: 1, step: 0.01, label: 'silhouette lock' }],
            ]);

            addLiquidGroup(fL, 'Heat', [
                ['heatSpeedRef', { min: 0.02, max: 1.5, step: 0.01, label: 'speed ref (calor=1)' }],
                ['heatCurve', { min: 0.2, max: 3, step: 0.05, label: 'curve' }],
                ['heatDecay', { min: 0.05, max: 5, step: 0.05, label: 'decay (1/s)' }],
                ['inkAmount', { min: 0, max: 1, step: 0.01, label: 'ink amount' }],
                ['inkDissipation', { min: 0, max: 5, step: 0.05, label: 'ink dissipation (1/s)' }],
            ]);

            addLiquidGroup(fL, 'Material', [
                ['colorTop', {}], ['colorBottom', {}],
                ['colorMid1', {}], ['colorMid2', {}], ['colorMax', {}],
            ]);
        }

        // ── UNDERWATER · Fireflies — 3 llums bioluminescents flotant dins l'esfera (sistema
        // separat de LiquidSphere, veure Fireflies.js). Sense uniforms de GPU: els bindings muten
        // FIREFLY_SETTINGS directament, sense cap callback 'change' — loop() ja el llegeix en viu
        // cada frame.
        if (scene._fireflies) {
            const fF = addF(underwater, 'Fireflies');
            [
                ['size', { min: 0.002, max: 0.06, step: 0.001, label: 'size (nucli)' }],
                ['haloSize', { min: 1, max: 15, step: 0.5, label: 'halo size (× nucli)' }],
                ['glowIntensity', { min: 0, max: 4, step: 0.05, label: 'glow intensity' }],
                ['speed', { min: 0.005, max: 0.3, step: 0.005, label: 'speed (viatge)' }],
                ['steering', { min: 0.05, max: 5, step: 0.05, label: 'steering (gir)' }],
                ['drift', { min: 0, max: 3, step: 0.05, label: 'drift (aleatorietat)' }],
                ['safetyRadius', { min: 0.2, max: 0.95, step: 0.01, label: 'safety radius' }],
                ['boundaryForce', { min: 0, max: 15, step: 0.1, label: 'boundary force' }],
                ['floatAmplitude', { min: 0, max: 0.05, step: 0.001, label: 'float · amplitude' }],
                ['floatSpeed', { min: 0, max: 3, step: 0.05, label: 'float · speed' }],
            ].forEach(([key, cfg]) => {
                if (FIREFLY_SETTINGS[key] === undefined) return;
                fF.addBinding(FIREFLY_SETTINGS, key, cfg);
            });
        }


        this._sphereFolders = folders;
    }

    static unmountSphere() {
        if (!this._sphereFolders) return;
        this._sphereFolders.forEach((f) => f.dispose());
        this._sphereFolders = null;
    }

    static unmountScene() {
        if (!isDebug && !this.pane) return;
        if (this._sceneRaf) { cancelAnimationFrame(this._sceneRaf); this._sceneRaf = null; }
        this._sphereFolders = null; // filles d'Underwater: el dispose de sota ja les elimina
        this._underwaterFolder = null;
        this._sceneFolders.forEach((f) => f.dispose());
        this._sceneFolders = [];
        // Si no queda cap altra carpeta al pane compartit, el tanquem.
        if (this.pane && this.pane.children.length === 0) this.dispose();
    }
}
